/**
 * ProjectTemplateDetailsUI - Presentational component for project template details
 *
 * This component handles only UI rendering. All API calls and business logic
 * should be handled by the parent shell component.
 *
 * TDM Tables (Tasks, Milestones, Deliverables) are passed as render props
 * so the parent shell can inject environment-specific shell-wrapped versions.
 */

import { CloudArrowUpIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import Confirm from '@/components/UI/Modal/Confirm';
import { formatDate3 } from '@/utils';
import CSVUploadModal from '../../ProjectTemplateDetails/CSVUploadModal';
import EditProjectTemplate from '../../ProjectTemplateDetails/EditProjectTemplate';
import type { NavItem, ProjectTemplateDetailsUIProps } from './types';

const navItems: NavItem[] = [
  { name: 'Acquisition', slug: 'ACQUISITION' },
  { name: 'Finance', slug: 'FINANCE' },
  { name: 'Procurement', slug: 'PROCUREMENT' },
  { name: 'Development', slug: 'DEVELOPMENT' },
  { name: 'Design', slug: 'DESIGN' },
  { name: 'Construction', slug: 'CONSTRUCTION' },
];

export const ProjectTemplateDetailsUI = ({
  template,
  isLoading,
  onEdit,
  onDelete,
  onCSVUpload,
  isDeleting,
  isCSVUploading,
  hasPermission,
  renderTasksTable,
  renderDeliverablesTable,
  renderMilestonesTable,
}: ProjectTemplateDetailsUIProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showCSVUploadModal, setShowCSVUploadModal] = useState<boolean>(false);
  const [showModal, setShowModal] = useState({
    editTemplateForm: false,
  });

  const [activeNav, setActiveNav] = useState<NavItem>({
    name: 'Acquisition',
    slug: 'ACQUISITION',
  });

  const handleNavigation = (item: NavItem) => {
    setActiveNav(item);
  };

  const handleEditModal = () => {
    setShowModal((prevState) => ({
      ...prevState,
      editTemplateForm: !prevState.editTemplateForm,
    }));
  };

  const handleCSVUploadModal = () => {
    setShowCSVUploadModal((prev) => !prev);
  };

  const handleCSVSubmit = (parsedData: Record<string, any>[]) => {
    onCSVUpload(parsedData);
    setShowCSVUploadModal(false);
  };

  const confirmDeleteProjectTemplate = async () => {
    if (template?.id === 0) {
      return;
    }
    onDelete();
    setShowDeleteConfirm(false);
  };

  const cancelDeleteProjectTemplate = () => {
    setShowDeleteConfirm(false);
  };

  if (isLoading || !template) {
    return <div>Loading...</div>;
  }

  const canEdit = template.canEdit !== false;

  return (
    <>
      <div className='flex justify-between items-start mb-5'>
        <ul className='flex flex-col justify-between gap-2 p-5'>
          <li className='max-w-xl'>
            <h2 className='m-0'>{template.name}</h2>
            <span className='text-sm break-words'>{template.description}</span>
          </li>
          <li className='flex gap-4'>
            <span>
              Construction Type: <strong>{template.constructionType}</strong>
            </span>
            <span>
              Project Type: <strong>{template.projectType}</strong>
            </span>
          </li>
          <li className='flex gap-4 italic text-xs'>
            <span>
              Created By: <strong>{template.createdBy}</strong>
            </span>
            <span>
              Created on: <strong>{template.createdAt && formatDate3(template.createdAt)}</strong>
            </span>
            <span>
              Last updated: <strong>{template.updatedAt && formatDate3(template.updatedAt)}</strong>
            </span>
          </li>
        </ul>
        <div className='flex gap-2'>
          {canEdit && hasPermission(['UPDATE_PROJECT_TEMPLATE']) && (
            <span
              className='p-2  text-neutralColor-normal hover:text-primaryColor-normal  hover:bg-neutralColor-lighter rounded-full cursor-pointer transition duration-300 ease-in-out'
              onClick={handleCSVUploadModal}
              title='Upload CSV'
            >
              <CloudArrowUpIcon className='h-5 w-5' />
            </span>
          )}
          {canEdit && hasPermission(['UPDATE_PROJECT_TEMPLATE']) && (
            <span
              className='p-2  text-neutralColor-normal hover:text-primaryColor-normal  hover:bg-neutralColor-lighter rounded-full cursor-pointer transition duration-300 ease-in-out'
              onClick={handleEditModal}
            >
              <PencilIcon className='h-5 w-5' />
            </span>
          )}
          {canEdit && hasPermission(['DELETE_PROJECT_TEMPLATE']) && (
            <span
              className='p-2 text-neutralColor-normal hover:text-tertiaryColor-normal  hover:bg-neutralColor-lighter rounded-full cursor-pointer transition duration-300 ease-in-out'
              onClick={() => setShowDeleteConfirm(true)}
            >
              <TrashIcon className='h-5 w-5' />
            </span>
          )}
        </div>
      </div>
      <div className='flex w-full justify-between border-b-2'>
        {navItems.map((item) => (
          <span
            key={item.slug}
            className={`${
              activeNav.slug === item.slug
                ? 'text-primaryColor-normal border-b-2 border-primaryColor-normal pb-2'
                : 'text-neutralColor-normal'
            } cursor-pointer px-5`}
            onClick={() => handleNavigation(item)}
          >
            {item.name}
          </span>
        ))}
      </div>
      <div className='my-5 space-y-10'>
        {renderTasksTable(activeNav)}
        {renderDeliverablesTable(activeNav)}
        {renderMilestonesTable(activeNav)}
      </div>
      {showDeleteConfirm && (
        <Confirm
          title='Delete Template'
          message={`You are about to delete project template ${template.name}. Are you sure you want to delete ?`}
          acceptButtonLabel={`${isDeleting ? 'Deleting...' : 'Delete'}`}
          declineButtonLabel='Cancel'
          accept={confirmDeleteProjectTemplate}
          decline={cancelDeleteProjectTemplate}
        />
      )}

      {showModal.editTemplateForm && <EditProjectTemplate handleModal={handleEditModal} defaultValues={template} />}

      {showCSVUploadModal && (
        <CSVUploadModal
          onHide={handleCSVUploadModal}
          onSubmit={handleCSVSubmit}
          title='Upload Project Template CSV'
          isSubmitting={isCSVUploading}
        />
      )}
    </>
  );
};

export default ProjectTemplateDetailsUI;
